import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faRobot,
  faPlus,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../contexts/AuthContext';
import botService from '../services/botService';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import './Header.css';
import './LanguageSwitcher.css';
import { useTranslation } from 'react-i18next';

const Header = ({ onMenuClick }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  useContext(AuthContext);
  const [bots, setBots] = useState([]);
  const [showBotMenu, setShowBotMenu] = useState(false);
  const [loadingBots, setLoadingBots] = useState(false);
  const [currentBot, setCurrentBot] = useState(null);
  const [loadingCurrentBot, setLoadingCurrentBot] = useState(false);

  useEffect(() => {
    loadBots();
    loadCurrentBot();
  }, [location.pathname]);

  // Recarrega o bot atual quando o selectedBotId mudar (de outras abas)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedBotId') {
        loadCurrentBot();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const loadBots = async () => {
    try {
      setLoadingBots(true);
      const data = await botService.getAllBots();
      setBots(data);
    } catch (err) {
      console.error('Error loading bots:', err);
    } finally {
      setLoadingBots(false);
    }
  };

  const loadCurrentBot = async () => {
    try {
      const storedBotId = localStorage.getItem('selectedBotId');
      if (storedBotId) {
        setLoadingCurrentBot(true);
        const bot = await botService.getBotById(storedBotId);
        
        // Tenta obter o username do bot_info se disponível
        try {
          const status = await botService.getBotStatus(storedBotId);
          if (status.bot_info && status.bot_info.username) {
            bot.username = status.bot_info.username;
          }
        } catch (statusErr) {
          // Se não conseguir obter o status, continua sem username
          console.warn('Could not load bot status for username:', statusErr);
        }
        
        setCurrentBot(bot);
      } else {
        setCurrentBot(null);
      }
    } catch (err) {
      console.error('Error loading current bot:', err);
      setCurrentBot(null);
    } finally {
      setLoadingCurrentBot(false);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return t('header.title.dashboard');
    if (path === '/billing') return t('header.title.billing');
    if (path.startsWith('/payment-status/')) return t('header.title.payment_status');
    if (path === '/create-bot') return t('header.title.create_bot');
    if (path === '/bot/update') return t('header.title.update-bot');
    if (path.startsWith('/bot/update/')) return t('header.title.update-bot');
    if (path.startsWith('/bot-list')) return t('header.title.my_bots');
    if (path.startsWith('/settings/profile')) return t('header.title.profile');
    if (path.startsWith('/settings/payment-cycles')) return t('header.title.payment-cycles');
    if (path.startsWith('/settings/payment-gateways')) return t('header.title.payment-gateways');
    if (path.startsWith('/settings/security')) return t('header.title.security');
    if (path.startsWith('/settings/storage')) return t('header.title.storage');
    if (path.startsWith('/settings/artisan')) return t('header.title.artisan-commands');
    if (path.startsWith('/settings/cron-jobs')) return t('header.title.cron-jobs');
    if (path.startsWith('/settings/laravel-logs')) return t('header.title.laravel-logs');
    if (path === '/users') return t('header.title.users');
    if (path === '/user-groups') return t('header.title.user-groups');
    if (path === '/logs') return t('header.title.logs');
    if (path === '/ftp') return t('header.title.ftp-manager');

    if (path.includes('/manage-bot')) {
      return t('header.title.manage_bot');
    }
    if (path === '/results/contacts') return 'Contatos';
    if (path === '/marketing/alerts') return 'Alertas';
    if (path === '/marketing/downsell') return 'Downsell';
    if (path === '/marketing') return 'Marketing';
    if (path === '/settings/users') return t('header.title.users');
    if (path === '/group-management') return 'Gerenciamento de Grupo';
    return 'Pagina Inicial';
  };

  const getPageSubtitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Visão geral dos seus recebimentos e transações';
    if (path === '/billing') return 'Consulte e gerencie seus pagamentos';
    if (path.startsWith('/payment-status/')) return 'Detalhes do status do pagamento';
    if (path === '/create-bot') return 'Preencha os detalhes para criar um novo bot';
    if (path.startsWith('/bot-list')) return 'Veja e gerencie todos os seus bots';
    if (path === '/results/contacts') return 'Visualize e gerencie seus contatos';
    if (path === '/settings/payment-cycles') return 'Gerencie os ciclos de pagamento';
    if (path === '/settings/payment-gateways') return 'Gerencie os gateways de pagamento';
    if (path === '/settings/security') return 'Configure a autenticação de dois fatores para maior segurança';
    if (path === '/settings/storage') return 'Gerencie seus arquivos e armazenamento';
    if (path === '/settings/artisan') return 'Execute comandos Artisan diretamente do painel';
    if (path === '/settings/cron-jobs') return 'Configure e monitore seus trabalhos cron';
    if (path === '/settings/laravel-logs') return 'Visualize e gerencie os logs do Laravel';
    if (path === '/settings/users') return 'Gerencie os usuarios da sua plataforma';
    if (path === '/settings/user-groups') return 'Organize usuarios em grupos para melhor gerenciamento';
    if (path === '/settings/logs') return 'Revise as atividades e eventos do sistema';
    if (path === '/settings/ftp-manager' || path === '/ftp') return 'Acesse e gerencie seus arquivos via FTP';
    if (path === '/group-management') return 'Gerencie os membros dos grupos do seu bot';


    if (path.startsWith('/settings/profile')) return 'Atualize suas informações pessoais e preferências';
    if (path.includes('/manage-bot/')) {
      return 'Gerencie as configurações e funcionalidades do seu bot';
    }
    return 'Informações e ações da página atual';
  };

  const handleBotSelect = async (botId) => {
    localStorage.setItem('selectedBotId', botId.toString());
    setShowBotMenu(false);
    await loadCurrentBot(); // Atualiza o bot exibido no header
    navigate(`/manage-bot/${botId}/settings`);
    // Dispara evento customizado para atualizar outros componentes
    window.dispatchEvent(new Event('botSelected'));
  };

  const handleCreateBot = () => {
    navigate('/create-bot');
    setShowBotMenu(false);
  };


  return (
    <div className="header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onMenuClick} aria-label="Menu">
          <FontAwesomeIcon icon={faBars} />
        </button>

        <div>
          <h1 className="dashboard-main-title">{getPageTitle()}</h1>
          <p className="dashboard-subtitle">{getPageSubtitle()}</p>
        </div>


      </div>
      <div className="header-right">
        <ThemeToggle />
        <LanguageSwitcher />
        {loadingCurrentBot ? (
          <div className="current-bot-info loading">
            <div className="current-bot-icon">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            <div className="current-bot-details">
              <div className="current-bot-name">Carregando...</div>
            </div>
          </div>
        ) : currentBot ? (
          <div className="current-bot-info">
            <div className="current-bot-icon">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            <div className="current-bot-details">
              <div className="current-bot-name">{currentBot.name}</div>
              {currentBot.username && (
                <div className="current-bot-username">@{currentBot.username}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="current-bot-info no-bot">
            <div className="current-bot-icon">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            <div className="current-bot-details">
              <div className="current-bot-name">Nenhum bot selecionado</div>
            </div>
          </div>
        )}
        <div className="header-profile-menu">
          <button 
            className="profile-circle-btn"
            onClick={() => setShowBotMenu(!showBotMenu)}
            title="Meus bots"
          >
            <FontAwesomeIcon icon={faRobot} />
          </button>

          {showBotMenu && (
            <>
              <div 
                className="menu-overlay" 
                onClick={() => setShowBotMenu(false)}
              ></div>
              <div className="bots-dropdown-menu">
                <div className="dropdown-header">
                  <h3>Meus bots</h3>
                  <button 
                    className="close-btn"
                    onClick={() => setShowBotMenu(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                {loadingBots ? (
                  <div className="dropdown-loading">Carregando...</div>
                ) : bots.length === 0 ? (
                  <div className="dropdown-empty">
                    <p>Nenhum bot criado</p>
                    <button onClick={handleCreateBot} className="btn-create-in-menu">
                      + Criar bot
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="bots-dropdown-list">
                      {bots.map((bot) => (
                        <button
                          key={bot.id}
                          className={`bot-menu-item ${location.pathname === `/manage-bot/${bot.id}/settings` ? 'active' : ''}`}
                          onClick={() => handleBotSelect(bot.id)}
                        >
                          <div className="bot-menu-icon">
                            <FontAwesomeIcon icon={faRobot} style={{ color: '#9333ea' }} />
                          </div>
                          <span className="bot-menu-name">{bot.name}</span>
                          {location.pathname === `/manage-bot/${bot.id}/settings` && (
                            <span className="bot-menu-active">Ativo</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="dropdown-footer">
                      <button onClick={handleCreateBot} className="btn-create-new-menu">
                        <FontAwesomeIcon icon={faPlus} />
                        Criar novo bot
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
